import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Power, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Home, Menu, Undo2,
  Volume1, Volume2, VolumeX, Play, Pause, Square, Rewind, FastForward, Plus, Minus,
} from "lucide-react";
import api from "@/lib/api";

const INPUTS = [["dtv", "TV"], ["HDMI1", "HDMI 1"], ["HDMI2", "HDMI 2"], ["HDMI3", "HDMI 3"]];
const APPS = [["3201907018807", "Netflix"], ["111299001912", "YouTube"], ["3201910019365", "Prime Video"], ["3201901017640", "Disney+"]];

export const sendTV = async (capability, command, args = []) => {
  try {
    await api.post("/admin/tv/command", { capability, command, arguments: args });
    return true;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Befehl fehlgeschlagen");
    return false;
  }
};

const Key = ({ onClick, children, testid, className = "" }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center justify-center rounded-lg border border-[#2A2A2A] bg-[#0A0A0A] text-[#F5F0E6] hover:border-[#D4AF37] hover:text-[#EEDC82] active:scale-95 transition-[transform,border-color,color] duration-150 ${className}`}>
    {children}
  </button>
);

export default function TVRemote({ status, onChanged }) {
  const [vol, setVol] = useState(status?.volume ?? 20);
  useEffect(() => { if (status?.volume != null) { setVol(status.volume); } }, [status?.volume]);
  const isOn = status?.power === "on";
  const after = (ok) => { if (ok) { setTimeout(onChanged, 800); } };
  const remote = (k) => sendTV("samsungvd.remoteControl", "send", [k]);

  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="tv-remote">
      <div className="card-lux p-5 flex flex-col items-center gap-6">
        <button onClick={async () => after(await sendTV("switch", isOn ? "off" : "on"))} data-testid="tv-power-btn"
          className={`w-24 h-24 rounded-full border-2 flex flex-col items-center justify-center gap-1 active:scale-95 transition-[transform,background-color] ${isOn ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#EEDC82] shadow-[0_0_30px_rgba(212,175,55,0.25)]" : "border-[#2A2A2A] text-[#A8A398]"}`}>
          <Power size={30} />
          <span className="text-[9px] uppercase tracking-widest">{isOn ? "An" : status?.power === "off" ? "Aus" : "Power"}</span>
        </button>
        <div className="grid grid-cols-3 gap-2 w-56" data-testid="tv-dpad">
          <span />
          <Key onClick={() => remote("UP")} testid="tv-key-up" className="h-14"><ChevronUp /></Key>
          <span />
          <Key onClick={() => remote("LEFT")} testid="tv-key-left" className="h-14"><ChevronLeft /></Key>
          <Key onClick={() => remote("OK")} testid="tv-key-ok" className="h-14 rounded-full border-[#D4AF37] text-[#D4AF37] font-heading">OK</Key>
          <Key onClick={() => remote("RIGHT")} testid="tv-key-right" className="h-14"><ChevronRight /></Key>
          <span />
          <Key onClick={() => remote("DOWN")} testid="tv-key-down" className="h-14"><ChevronDown /></Key>
          <span />
        </div>
        <div className="grid grid-cols-3 gap-2 w-56">
          <Key onClick={() => remote("BACK")} testid="tv-key-back" className="h-11 gap-1 text-[10px] uppercase"><Undo2 size={14} /> Zurück</Key>
          <Key onClick={() => remote("HOME")} testid="tv-key-home" className="h-11 gap-1 text-[10px] uppercase"><Home size={14} /> Home</Key>
          <Key onClick={() => remote("MENU")} testid="tv-key-menu" className="h-11 gap-1 text-[10px] uppercase"><Menu size={14} /> Menü</Key>
        </div>
      </div>

      <div className="space-y-5">
        <VolumeCard vol={vol} setVol={setVol} muted={status?.mute === "muted"} after={after} />
        <div className="card-lux p-5 space-y-3">
          <Label>Kanal & Wiedergabe</Label>
          <div className="grid grid-cols-2 gap-2">
            <Key onClick={() => sendTV("tvChannel", "channelDown")} testid="tv-channel-down" className="h-11 gap-1 text-xs"><Minus size={14} /> Kanal</Key>
            <Key onClick={() => sendTV("tvChannel", "channelUp")} testid="tv-channel-up" className="h-11 gap-1 text-xs"><Plus size={14} /> Kanal</Key>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {[["rewind", Rewind], ["play", Play], ["pause", Pause], ["stop", Square], ["fastForward", FastForward]].map(([c, I]) => (
              <Key key={c} onClick={() => sendTV("mediaPlayback", c)} testid={`tv-media-${c}`} className="h-11"><I size={16} /></Key>
            ))}
          </div>
        </div>
        <div className="card-lux p-5 space-y-3">
          <Label>Quelle</Label>
          <div className="flex flex-wrap gap-2">
            {INPUTS.map(([v, l]) => (
              <button key={v} onClick={async () => after(await sendTV("samsungvd.mediaInputSource", "setInputSource", [v]))}
                data-testid={`tv-input-${v}`}
                className={`px-4 py-2 rounded-full border text-xs ${status?.input === v ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#EEDC82]" : "border-[#2A2A2A] text-[#F5F0E6] hover:border-[#D4AF37]"}`}>
                {l}
              </button>
            ))}
          </div>
          <Label>Apps</Label>
          <div className="grid grid-cols-2 gap-2">
            {APPS.map(([id, name]) => (
              <Key key={id} onClick={() => sendTV("custom.launchapp", "launchApp", [id])} testid={`tv-app-${id}`} className="h-11 text-sm">{name}</Key>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{children}</div>;

function VolumeCard({ vol, setVol, muted, after }) {
  return (
    <div className="card-lux p-5 space-y-3" data-testid="tv-volume-card">
      <div className="flex items-center justify-between">
        <Label>Lautstärke</Label>
        <span className="font-heading text-2xl text-[#EEDC82]" data-testid="tv-volume-value">{vol}</span>
      </div>
      <div className="flex items-center gap-2">
        <Key onClick={async () => { if (await sendTV("audioVolume", "volumeDown")) { setVol((v) => Math.max(0, v - 1)); } }} testid="tv-vol-down" className="h-11 w-11 shrink-0"><Volume1 size={16} /></Key>
        <input type="range" min={0} max={100} value={vol} onChange={(e) => setVol(Number(e.target.value))}
          onMouseUp={async (e) => after(await sendTV("audioVolume", "setVolume", [Number(e.target.value)]))}
          onTouchEnd={async (e) => after(await sendTV("audioVolume", "setVolume", [Number(e.target.value)]))}
          className="flex-1 accent-[#D4AF37]" data-testid="tv-vol-slider" />
        <Key onClick={async () => { if (await sendTV("audioVolume", "volumeUp")) { setVol((v) => Math.min(100, v + 1)); } }} testid="tv-vol-up" className="h-11 w-11 shrink-0"><Volume2 size={16} /></Key>
      </div>
      <Key onClick={async () => after(await sendTV("audioMute", muted ? "unmute" : "mute"))} testid="tv-mute-btn"
        className={`h-11 w-full gap-2 text-xs uppercase tracking-widest ${muted ? "border-[#D4AF37] text-[#EEDC82]" : ""}`}>
        <VolumeX size={15} /> {muted ? "Ton an" : "Stumm"}
      </Key>
    </div>
  );
}
